import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { combineLatestWith, distinctUntilChanged, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  ActionTypeEnum,
  AreaTypeEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import {
  AnswerWithMoreInfoModel,
  NavigateToEnum
} from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { path } from '@bo-client/shared/constants/path';
import { ConnectionObjectForm } from '@bo-client/shared/models/connectionObject';
import { ContactDataForm } from '@bo-client/shared/models/contactDataDto';
import { GisDataEvent } from '@bo-client/shared/components/get-gis-data/get-gis-data.component';
import { ExternalCommissioningRequestModel } from '@bo-client/activation-management/shared/models/ExternalCommissioningRequestModel';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { uri } from '@bo-client/shared/constants/uri';
import { FieldConfigEnum } from '@bo-client/shared/constants/enums';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-grid-disconnection-page',
  templateUrl: './grid-disconnection-page.component.html',
  styleUrls: ['./grid-disconnection-page.component.scss']
})
export class GridDisconnectionPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  constructor(
    private httpHapService: HttpHapService,
    private dataShareService: DataShareService,
    private router: Router,
    private fb: FormBuilder,
    private createFormsService: CreateFormsService,
    private toastService: ToastrService
  ) {}

  formGroup$: Observable<GridDisconnectionForm>;

  errors: ShowErrorModel[] = [];
  firstDateAvailable: Date = new Date();
  summary$: Observable<ExternalCommissioningRequestModel>;
  commentConfig$: Observable<FieldConfigEnum>;

  companyLocationsByTenant$: Observable<CompanyLocationDto[]>;
  eventsFromParent$ = of('');

  selectedConnectionObject$: BehaviorSubject<GisDataModel> = new BehaviorSubject(null);

  ngOnInit(): void {
    this.commentConfig$ = this.dataShareService
      .getSingleTenantConfig$<FieldConfigEnum>(
        'commissioning.gasConfig.externalGridDisconnectionCommentField'
      )
      .pipe(takeUntilDestroyed(this.destroyRef), shareReplay(1));
    this.companyLocationsByTenant$ = this.httpHapService.getTyped<CompanyLocationDto[]>(
      uri.companyLocationByCurrentTenant
    );
    this.firstDateAvailable = this.setFirstAvailableDate(
      this.dataShareService.selectedTenant.supplyAppointmentDays
    );
    this.formGroup$ = this.commentConfig$.pipe(
      map((commentConfig) =>
        this.fb.group({
          customer: this.fb.array([this.createFormsService.createContactDataForm({})]),
          connectionObject: this.createFormsService.connectionObjectFormWithValidator({}, null),
          connectionObjectValid: [false, Validators.requiredTrue],
          acceptConditions: [false, [Validators.requiredTrue]],
          meterNumber: ['', [Validators.required]],
          supplyAppointment: this.createFormsService.supplyAppointment({
            supplyAppointment: this.firstDateAvailable,
            supplyAppointmentWishDate: null,
            supplyAppointmentWishPartday: null
          }),
          comment: ['', [...(commentConfig === FieldConfigEnum.REQUIRED ? [Validators.required] : [])]]
        })
      ),
      combineLatestWith(
        this.dataShareService.getSingleTenantConfig$<boolean>(
          'commissioning.gasConfig.deactivateSupplyWishDate'
        )
      ),
      tap(([formGroup, deactivateSupplyWishDate]) => {
        const { supplyAppointmentWishDate, supplyAppointmentWishPartday } =
          formGroup.controls.supplyAppointment.controls;
        if (!deactivateSupplyWishDate) {
          supplyAppointmentWishDate.setValidators([
            Validators.required,
            this.minDateValidator(this.firstDateAvailable)
          ]);
          supplyAppointmentWishPartday.setValidators([Validators.required]);
        } else {
          supplyAppointmentWishDate.clearValidators();
          supplyAppointmentWishPartday.clearValidators();
        }
        this.summary$ = formGroup.valueChanges.pipe(
          startWith(formGroup.value),
          distinctUntilChanged((prev, curr) => {
            return prev.meterNumber === curr.meterNumber;
          }),
          map((formValue) => this.formValueToDto(formValue)),
          shareReplay(1)
        );
      }),
      map(([formGroup]) => formGroup),
      shareReplay(1)
    );
  }

  submit(formGroup: GridDisconnectionForm) {
    if (formGroup.invalid) {
      if (formGroup.controls.acceptConditions.invalid) {
        this.toastService.error('Bitte akzeptieren Sie die Datenschutzbedingungen.');
      }
      return;
    }

    const data = this.formValueToDto(formGroup.value);

    this.httpHapService.post(data, '/external/commissioningrequest/disconnection').subscribe({
      next: (result) => {
        this.toastService.success('Vielen Dank, wir haben Ihre Daten erhalten.');
        this.navigateTo(result);
        // ROUTE TO HOME
      },
      error: (err) => {
        this.toastService.error('Beim Übermitteln ist ein Fehler aufgetreten: ' + err);
      }
    });
  }

  onGisDataChanged(
    $event: GisDataEvent & { currentCompanyLocationFromGis: CompanyLocationDto },
    validityControl: FormControl<boolean>
  ) {
    this.selectedConnectionObject$.next($event.gisData);
    validityControl.setValue($event.areaAvailable);
  }

  navigateTo(answer: AnswerWithMoreInfoModel) {
    if (answer.navigateTo !== NavigateToEnum.none) {
      this.router
        .navigate([path.external + '/' + answer.navigateTo + '/' + answer.message], {
          replaceUrl: true
        })
        .then();
    }
  }

  get privacyPolicyUrl() {
    return this.dataShareService.tenantConfig.common.privacyPolicyUrl || '';
  }

  private formValueToDto(formValue: GridDisconnectionForm['value']): ExternalCommissioningRequestModel {
    const gisData = this.selectedConnectionObject$.getValue();
    const requestModel = new ExternalCommissioningRequestModel(
      ActionTypeEnum.decommissioning,
      null,
      AreaTypeEnum.GAS
    );
    const { customer, meterNumber } = formValue;
    requestModel.customer = customer[0] || {};
    requestModel.actions[0].meterNumber = meterNumber || '';
    const { houseNo, houseNoAddition, ...rest } = gisData || {};
    requestModel.companyLocation.external_id = rest.orgEinheit; // orgEinheit wird in companyLocation und connectionObject benötigt
    requestModel.connectionObject = { ...rest, streetNo: houseNo, streetNoAddition: houseNoAddition };
    requestModel.supplyAppointmentWishPartday = formValue.supplyAppointment.supplyAppointmentWishPartday;
    requestModel.supplyAppointmentWishDate = formValue.supplyAppointment.supplyAppointmentWishDate;
    requestModel.supplyAppointment = formValue.supplyAppointment.date;
    requestModel.comment = formValue.comment;
    return requestModel;
  }

  // Aus LoadDataFormsService
  private setFirstAvailableDate(supplyAppointmentDays: number): Date {
    const toMilliSeconds = 24 * 60 * 60 * 1000;
    let availableDate: Date = new Date(new Date().getTime() + supplyAppointmentDays * toMilliSeconds);
    switch (availableDate.getDay()) {
      case 6: // Saturday
        availableDate = new Date(availableDate.getTime() + 2 * toMilliSeconds);
        break;
      case 0: // Sunday
        availableDate = new Date(availableDate.getTime() + toMilliSeconds);
        break;
    }
    availableDate.setHours(0, 0, 0, 0);
    return availableDate;
  }

  private minDateValidator(minDate: Date): ValidatorFn {
    return (control: FormControl) => {
      const selectedDateTime = control.value && new Date(control.value).getTime();
      if (selectedDateTime && selectedDateTime < minDate.getTime()) {
        return { minDate: true }; // Validation error
      }
      return null;
    };
  }

  protected readonly AreaTypeEnum = AreaTypeEnum;
  protected readonly FieldConfigEnum = FieldConfigEnum;
}

type GridDisconnectionForm = FormGroup<{
  customer: FormArray<ContactDataForm>;
  connectionObject: ConnectionObjectForm;
  connectionObjectValid: FormControl<boolean>;
  acceptConditions: FormControl<boolean>;
  meterNumber: FormControl<string>;
  supplyAppointment: FormGroup<{
    date: FormControl<Date>;
    time: FormControl<string>;
    supplyAppointmentWishDate: FormControl<Date>;
    supplyAppointmentWishPartday: FormControl<string>;
  }>;
  comment: FormControl<string>;
}>;
